import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { getPromptToastErrorMessage, promptRequest } from '@/hooks/prompts/lib'
import { promptQueryKeys, promptsQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  SavedPrompt,
  UpdateSavedPrompt,
} from '@/types/library/SavedPrompt'

interface UseUpdateSavedPromptArgs {
  orgId: UUID | ''
}

interface UpdateSavedPromptRequest {
  promptId: UUID
  data: UpdateSavedPrompt
}

const updateSavedPrompt = async (
  promptId: UUID,
  data: UpdateSavedPrompt,
  accessToken: string | undefined | null,
): Promise<SavedPrompt> =>
  promptRequest<SavedPrompt>(
    'PUT',
    `${API_URL}/api/v1/prompts/${promptId}`,
    accessToken,
    data,
  )

const recordsPlaybookActivity = (changes: UpdateSavedPrompt): boolean =>
  changes.name !== undefined ||
  changes.description !== undefined ||
  changes.content !== undefined ||
  changes.unrestricted !== undefined ||
  changes.archived !== undefined

export const useUpdatePrompt = ({ orgId }: UseUpdateSavedPromptArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    SavedPrompt,
    Error,
    UpdateSavedPromptRequest,
    {
      revertFields: Partial<SavedPrompt>
      hadPrompt: boolean
    }
  >({
    mutationFn: ({ promptId, data }) =>
      updateSavedPrompt(promptId, data, accessToken),
    onMutate: async ({ promptId, data }) => {
      const key = promptsQueryKeys.organization({ orgId })
      const singleKey = promptQueryKeys.prompt({ promptId })
      await queryClient.cancelQueries({ queryKey: key })
      await queryClient.cancelQueries({ queryKey: singleKey, exact: true })

      const previousData =
        queryClient.getQueryData<PaginatedResponse<SavedPrompt>>(key)
      const previousPrompt = queryClient.getQueryData<SavedPrompt>(singleKey)
      const previousRow =
        previousPrompt ?? previousData?.data.find((p) => p.id === promptId)

      // Map UpdateSavedPrompt fields to SavedPrompt fields
      const mapped: Partial<SavedPrompt> = {
        ...(data.name !== undefined && { name: data.name }),
        ...(data.content !== undefined && { content: data.content }),
        ...(data.description !== undefined && {
          description: data.description,
        }),
        ...(data.unrestricted !== undefined && {
          has_unrestricted_access: data.unrestricted,
        }),
        ...(data.archived !== undefined && { archived: data.archived }),
        ...(data.enabled !== undefined && { enabled: data.enabled }),
      }

      // Rollback is field-aware: concurrent mutations touch disjoint fields
      // (enabled vs shared vs archive), so reverting only THIS mutation's
      // fields cannot clobber another mutation's successful result.
      const revertFields: Partial<SavedPrompt> = {}
      if (previousRow) {
        for (const field of Object.keys(mapped) as (keyof SavedPrompt)[]) {
          Object.assign(revertFields, { [field]: previousRow[field] })
        }
      }

      // optimistically update the list cache
      if (previousData) {
        const updatedData = previousData.data.map((p) =>
          p.id === promptId ? { ...p, ...mapped } : p,
        )
        queryClient.setQueryData<PaginatedResponse<SavedPrompt>>(key, {
          count: previousData.count,
          data: updatedData,
          has_more: previousData.has_more,
        })
      }

      // optimistically update the single-prompt cache (detail page reads
      // this, so toggles like share flip instantly)
      if (previousPrompt) {
        queryClient.setQueryData<SavedPrompt>(singleKey, {
          ...previousPrompt,
          ...mapped,
        })
      }

      return { revertFields, hadPrompt: previousPrompt !== undefined }
    },
    onError: (err, { promptId }, context) => {
      const key = promptsQueryKeys.organization({ orgId })
      const singleKey = promptQueryKeys.prompt({ promptId })
      const revertFields = context?.revertFields
      if (revertFields && Object.keys(revertFields).length > 0) {
        queryClient.setQueryData<PaginatedResponse<SavedPrompt>>(
          key,
          (current) =>
            current && {
              ...current,
              data: current.data.map((p) =>
                p.id === promptId ? { ...p, ...revertFields } : p,
              ),
            },
        )
        if (context.hadPrompt) {
          queryClient.setQueryData<SavedPrompt>(
            singleKey,
            (current) => current && { ...current, ...revertFields },
          )
        }
      }
      // Reconcile with the server regardless: partial reverts of stale
      // snapshots must never be the final word.
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: singleKey, exact: true })
      handleError(err, false, getPromptToastErrorMessage(err))
    },
    onSuccess: (prompt, { promptId, data: changes }) => {
      const listKey = promptsQueryKeys.organization({ orgId })
      const singleKey = promptQueryKeys.prompt({ promptId })
      queryClient.setQueryData<SavedPrompt>(singleKey, prompt)
      void queryClient.invalidateQueries({ queryKey: listKey })
      if (recordsPlaybookActivity(changes)) {
        void queryClient.invalidateQueries({
          queryKey: promptQueryKeys.activity({ promptId }),
        })
      }
    },
  })
}
